/**
 * CountBadge — the "how many are waiting" number.
 *
 * Two shapes, both lifted verbatim from the shell so a migration is a pure
 * refactor with no pixel movement:
 * - `pill` is the Sidebar's nav count (rounded-full, px-2, semibold).
 * - `corner` is MobileTabBar's TabBadge — the little white-on-color number that
 *   hangs off the top-right of an icon. It expects a positioned ancestor
 *   (`relative`) and is `aria-hidden`, because the surrounding control already
 *   spells the count out in its `aria-label`. Do not un-hide it there.
 *
 * Zero is not a badge: null/undefined/<=0 render nothing at all, so callers can
 * pass a possibly-absent count straight through without a guard of their own.
 * Counts above `max` (default 99) collapse to "99+" — the badge is a glance,
 * not a readout, and a four-digit number breaks the row.
 *
 * `alert` is the red variant, reserved for backlogs that are actually late
 * (overdue follow-ups, unanswered leads); everything else is `primary`.
 */

export interface CountBadgeProps {
  count: number | null | undefined;
  /** `primary` (default) or the red `alert` variant for late work. */
  tone?: 'primary' | 'alert';
  /** `pill` (default) sits in a row; `corner` hangs off an icon. */
  variant?: 'pill' | 'corner';
  /** Counts above this collapse to `${max}+` (default 99). */
  max?: number;
  /** Appended last — positioning/margins only, by convention. */
  className?: string;
}

const PILL_BASE = 'rounded-full px-2 py-0.5 text-xs font-semibold';
const PILL_TONES = {
  primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  alert: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
} as const;

const CORNER_BASE =
  'absolute -right-2.5 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white';
const CORNER_TONES = { primary: 'bg-primary-600', alert: 'bg-red-600' } as const;

export default function CountBadge({
  count,
  tone = 'primary',
  variant = 'pill',
  max = 99,
  className = '',
}: CountBadgeProps) {
  if (count === null || count === undefined || count <= 0) return null;
  const label = count > max ? `${max}+` : String(count);

  if (variant === 'corner') {
    return (
      <span aria-hidden="true" className={`${CORNER_BASE} ${CORNER_TONES[tone]} ${className}`}>
        {label}
      </span>
    );
  }

  return <span className={`${PILL_BASE} ${PILL_TONES[tone]} ${className}`}>{label}</span>;
}
